import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import type {ChatMessage, ModelMessage, UserMessage} from '@Components/ai-chat-window/ai-chat-window.types';
import {MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {UserMessageRow} from '@Components/ai-chat-window/components/user-message-row/user-message-row';
import {AssistantMessageRow} from '@Components/ai-chat-window/components/assistant-message-row/assistant-message-row';
import {FailedMessageRow} from '@Components/ai-chat-window/components/failed-message-row/failed-message-row';
import {groupMessagesIntoConversations, adjustLastConversationSpacing, scrollToBottom} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import styles from './chat-messages.module.scss';
import {DOTS_COLOR, ThreeDotsLoader} from '@Components/three-dots-loader/three-dots-loader';
import {useChatStream} from '@Components/ai-chat-window/hooks/useChatStream';
import type {ChatMessagesDisplayProps} from '@Components/ai-chat-window/components/chat-messages-display/chat-messages.types';

export function ChatMessages({
  messages,
  chatId,
  bubbleClassNames,
  onRetry,
  onMessageRefMount,
  onRegenerate,
  waitingForRefreshedChatToFinish = false,
}: ChatMessagesDisplayProps): ReactElement {
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const conversations = groupMessagesIntoConversations(messages);

  const {isStreaming} = useChatStream({
    chatId,
  });

  const handleRef = (container: HTMLDivElement | null): void => {
    messagesRef.current = container;
    if (container) {
      onMessageRefMount(container);
    }
  };

  useEffect(() => {
    if (messagesRef.current && conversations.length > 0) {
      requestAnimationFrame(() => {
        adjustLastConversationSpacing(messagesRef.current);
      });
    }
  }, [conversations.length]);

  useEffect(() => {
    if (messagesRef.current && conversations.length > 0) {
      scrollToBottom(messagesRef.current);
    }
  }, [messages]);

  const shouldShowWaitingLoader = (isLastConversation: boolean): boolean => {
    return isLastConversation && waitingForRefreshedChatToFinish && !isStreaming;
  };

  const getThreeDotsLoader = (isLastConversation: boolean): ReactElement | null => {
    if (!shouldShowWaitingLoader(isLastConversation)) {
      return null;
    }

    return (
      <div className={styles.singleMessageContainer}>
        <ThreeDotsLoader size={8} color={DOTS_COLOR.DARK} />
      </div>
    );
  };

  const getUserMessage = (message: UserMessage, messageKey: string, isLastMessage: boolean): ReactElement => {
    return (
      <div key={messageKey} className={styles.singleMessageContainer}>
        <UserMessageRow message={message} bubbleClassNames={bubbleClassNames} />
      </div>
    );
  };

  const getAssistantMessage = (message: ModelMessage, messageKey: string, isLastMessage: boolean): ReactElement => {
    return (
      <div key={messageKey} className={styles.singleMessageContainer}>
        <AssistantMessageRow message={message} chatId={chatId} bubbleClassNames={bubbleClassNames} onRegenerate={onRegenerate} isLastMessage={isLastMessage} />
      </div>
    );
  };

  const getDeveloperMessage = (message: ChatMessage, messageKey: string, isLastMessage: boolean): ReactElement | null => {
    if ('failureType' in message) {
      return (
        <div key={messageKey} className={styles.singleMessageContainer}>
          <FailedMessageRow message={message} onRetry={onRetry} showRetry={isLastMessage} />
        </div>
      );
    }

    return null;
  };

  const getChatMessage = (message: ChatMessage, messageKey: string, isLastMessage: boolean): ReactElement | null => {
    switch (message.role) {
      case MessageRole.USER:
        return getUserMessage(message, messageKey, isLastMessage);
      case MessageRole.ASSISTANT:
        return getAssistantMessage(message, messageKey, isLastMessage);
      case MessageRole.DEVELOPER:
        return getDeveloperMessage(message, messageKey, isLastMessage);
      // throw new Error(`Unhandled message with role DEVELOPER ${message}`); // zain todo: (wip) check if we'll have any case where we'll have to show developed messages in FE
      default:
        return null;
    }
  };

  return (
    <div id="pmw-chat-messages-container" ref={handleRef} className={`${styles.messages} ${chatId ? styles.chatHasMessages : ''}`} role="log" aria-live="polite">
      {conversations.map((conversation, convIndex) => {
        const isLastConversation = convIndex === conversations.length - 1;

        return (
          <div key={`conversation-${convIndex}`} className={styles.conversation} data-conversation={convIndex}>
            {conversation.map((message, msgIndex) => {
              const isLastMessage = isLastConversation && msgIndex === conversation.length - 1;
              const messageKey = `message-${message.role}-${convIndex}-${msgIndex}`;

              return getChatMessage(message, messageKey, isLastMessage);
            })}

            {getThreeDotsLoader(isLastConversation)}
          </div>
        );
      })}
    </div>
  );
}
